const toCanonicalTime = (hourText = '', minuteText = '', periodText = '') => {
  let hour = Number(hourText);
  const minute = Number(minuteText);
  const period = String(periodText).replace(/\./g, '').toUpperCase();
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || minute > 59) return '';
  if (period) {
    if (hour < 1 || hour > 12) return '';
    if (period === 'PM' && hour < 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
  } else if (hour > 23) {
    return '';
  }
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const extractExplicitTimes = (value = '') => {
  const text = String(value || '').trim();
  if (!text) return [];
  const times = [];
  const timePattern = /\b(\d{1,2})[:.](\d{2})\s*(a\.?m\.?|p\.?m\.?)?\b/gi;
  let match = timePattern.exec(text);
  while (match) {
    const canonicalTime = toCanonicalTime(match[1], match[2], match[3]);
    if (canonicalTime) times.push(canonicalTime);
    match = timePattern.exec(text);
  }
  return times;
};

export const normalizeReminderTimes = (values = []) => (
  [...new Set(values.flatMap(extractExplicitTimes))]
);

const scheduleTimes = (medication = {}) => {
  const scheduleValues = Array.isArray(medication.schedules)
    ? medication.schedules.map((schedule) => schedule?.time)
    : [];
  return [...new Set([
    ...scheduleValues,
    medication.sourceScheduleText,
    medication.schedule,
  ].flatMap(extractExplicitTimes))];
};

export const buildInitialReminderSchedule = (medication = {}) => {
  const firstDose = medication.schedules?.[0] || {};
  const common = {
    id: medication.id,
    active: false,
    paused: false,
    times: scheduleTimes(medication),
    weekdays: [],
    startDate: String(medication.courseStartDate || medication.startDate || ''),
    endDate: String(medication.courseEndDate || medication.endDate || ''),
    dose: String(firstDose.dose || medication.dose || ''),
    unit: String(firstDose.unit || medication.unit || ''),
  };

  switch (medication.scheduleType) {
    case 'as_needed':
      return { ...common, kind: 'prn' };
    case 'specific_days':
      return { ...common, kind: 'weekdays', weekdays: medication.selectedDays || [] };
    case 'every_x_hours':
      return {
        ...common,
        kind: 'interval_hours',
        intervalHours: Number(medication.intervalHours) || '',
        anchorTime: String(medication.intervalStartTime || ''),
      };
    case 'short_course':
      return { ...common, kind: 'daily' };
    case 'scheduled':
      return { ...common, kind: 'daily' };
    default:
      return { ...common, kind: '', times: [] };
  }
};

export const reminderScheduleNeedsConfirmation = (schedule = {}) => {
  if (schedule.kind === 'prn') return false;
  if (!schedule.kind) return true;
  if (schedule.kind === 'interval_hours') {
    return !(Number(schedule.intervalHours) > 0 && schedule.startDate && schedule.anchorTime);
  }
  if (!Array.isArray(schedule.times) || !schedule.times.some(Boolean)) return true;
  if (schedule.kind === 'weekdays' && !(schedule.weekdays || []).length) return true;
  if (schedule.kind === 'interval_days' && !(Number(schedule.intervalDays) > 0 && schedule.startDate)) return true;
  if (schedule.kind === 'weekly' && !schedule.startDate) return true;
  if (schedule.kind === 'monthly' && !(schedule.dayOfMonth || schedule.startDate)) return true;
  return false;
};

export const formatReminderSchedule = (schedule = {}) => {
  if (schedule.kind === 'prn') return 'As needed — no missed-dose reminders';
  if (reminderScheduleNeedsConfirmation(schedule)) return 'Schedule needs confirmation';
  if (schedule.kind === 'interval_hours') return `Every ${schedule.intervalHours} hours from ${schedule.anchorTime}`;
  if (schedule.kind === 'interval_days') return `Every ${schedule.intervalDays} days at ${(schedule.times || []).join(', ')}`;
  if (schedule.kind === 'weekly') return `Weekly at ${(schedule.times || []).join(', ')}`;
  if (schedule.kind === 'monthly') return `Monthly on day ${schedule.dayOfMonth || String(schedule.startDate).slice(-2)}`;
  if (schedule.kind === 'weekdays') return `${(schedule.weekdays || []).join(', ')} at ${(schedule.times || []).join(', ')}`;
  return `Daily at ${(schedule.times || []).join(', ')}`;
};
